import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Dialog,
  DialogContent,
  Fab,
  Skeleton,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
} from '@mui/icons-material';
import { Manual, ManualPage } from '../../types/manual';
import { manualsAPI } from '../../services/api';

interface PhotoManualViewerProps {
  manual: Manual;
  viewMode: 'pages' | 'gallery';
}

interface FullscreenState {
  open: boolean;
  currentPageIndex: number;
  zoom: number;
  pan: { x: number; y: number };
}

// Cache for image URLs to avoid repeated API calls
const imageUrlCache = new Map<string, string>();

const getAuthenticatedImageUrl = async (pageId: string, thumbnail: boolean = false): Promise<string> => {
  const cacheKey = `${pageId}-${thumbnail}`;
  
  // Return cached URL if available
  if (imageUrlCache.has(cacheKey)) {
    return imageUrlCache.get(cacheKey)!;
  }

  try {
    const response = await manualsAPI.getPageImage(pageId, thumbnail);
    const blob = response.data;
    const objectUrl = URL.createObjectURL(blob);
    
    // Cache the URL
    imageUrlCache.set(cacheKey, objectUrl);
    
    return objectUrl;
  } catch (error) {
    console.error('Failed to load image:', error);
    throw error;
  }
};

export const PhotoManualViewer: React.FC<PhotoManualViewerProps> = ({ manual, viewMode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(new Set());
  const [imageLoadingStates, setImageLoadingStates] = useState<Set<string>>(new Set());
  const [loadedImageUrls, setLoadedImageUrls] = useState<Map<string, string>>(new Map());
  const [fullscreen, setFullscreen] = useState<FullscreenState>({
    open: false,
    currentPageIndex: 0,
    zoom: 1,
    pan: { x: 0, y: 0 },
  });

  const pages = manual.pages || [];
  const currentPage = pages[fullscreen.currentPageIndex];

  // Use refs to avoid dependency loops
  const imageLoadingStatesRef = useRef(imageLoadingStates);
  const loadedImageUrlsRef = useRef(loadedImageUrls);
  
  // Update refs when state changes
  useEffect(() => {
    imageLoadingStatesRef.current = imageLoadingStates;
  }, [imageLoadingStates]);
  
  useEffect(() => {
    loadedImageUrlsRef.current = loadedImageUrls;
  }, [loadedImageUrls]);

  // Load image from API and store the object URL
  const loadImage = useCallback(async (pageId: string, thumbnail: boolean = false) => {
    const cacheKey = `${pageId}-${thumbnail}`;
    
    // Skip if already loading or loaded
    if (imageLoadingStatesRef.current.has(cacheKey) || loadedImageUrlsRef.current.has(cacheKey)) {
      return;
    }

    setImageLoadingStates(prev => new Set(prev).add(cacheKey));
    setImageLoadErrors(prev => {
      const newSet = new Set(prev);
      newSet.delete(cacheKey);
      return newSet;
    });

    try {
      const objectUrl = await getAuthenticatedImageUrl(pageId, thumbnail);
      setLoadedImageUrls(prev => new Map(prev).set(cacheKey, objectUrl));
    } catch (error) {
      console.error('Failed to load image:', pageId, thumbnail, error);
      setImageLoadErrors(prev => new Set(prev).add(cacheKey));
    } finally {
      setImageLoadingStates(prev => {
        const newSet = new Set(prev);
        newSet.delete(cacheKey);
        return newSet;
      });
    }
  }, []); // Empty dependency array prevents infinite loops

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      loadedImageUrls.forEach(url => URL.revokeObjectURL(url));
      imageUrlCache.forEach(url => URL.revokeObjectURL(url));
      imageUrlCache.clear();
    };
  }, [loadedImageUrls]);

  const openFullscreen = (pageIndex: number) => {
    setFullscreen({
      open: true,
      currentPageIndex: pageIndex,
      zoom: 0.5,
      pan: { x: 0, y: 0 },
    });
  };

  const closeFullscreen = () => {
    setFullscreen(prev => ({ ...prev, open: false }));
  };

  const navigatePage = useCallback((direction: 'prev' | 'next') => {
    setFullscreen(prev => {
      let newIndex = prev.currentPageIndex;
      if (direction === 'prev' && newIndex > 0) {
        newIndex = newIndex - 1;
      } else if (direction === 'next' && newIndex < pages.length - 1) {
        newIndex = newIndex + 1;
      }
      return {
        ...prev,
        currentPageIndex: newIndex,
        zoom: 0.5,
        pan: { x: 0, y: 0 },
      };
    });
  }, [pages.length]);

  const handleZoom = (direction: 'in' | 'out' | 'fit') => {
    setFullscreen(prev => {
      let newZoom = prev.zoom;
      if (direction === 'in' && newZoom < 3) {
        newZoom = Math.min(newZoom * 1.2, 3);
      } else if (direction === 'out' && newZoom > 0.5) {
        newZoom = Math.max(newZoom / 1.2, 0.5);
      } else if (direction === 'fit') {
        newZoom = 0.5;
      }
      return {
        ...prev,
        zoom: newZoom,
        pan: direction === 'fit' ? { x: 0, y: 0 } : prev.pan,
      };
    });
  };

  const handlePan = useCallback((deltaX: number, deltaY: number) => {
    setFullscreen(prev => ({
      ...prev,
      pan: {
        x: prev.pan.x + deltaX,
        y: prev.pan.y + deltaY,
      },
    }));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!fullscreen.open) return;
      
      switch (event.key) {
        case 'Escape':
          closeFullscreen();
          break;
        case 'ArrowLeft':
          navigatePage('prev');
          break;
        case 'ArrowRight':
          navigatePage('next');
          break;
        case 'ArrowUp':
          handlePan(0, 20);
          break;
        case 'ArrowDown':
          handlePan(0, -20);
          break;
        case '+':
        case '=':
          handleZoom('in');
          break;
        case '-':
          handleZoom('out');
          break;
        case '0':
          handleZoom('fit');
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [fullscreen.open, handlePan, navigatePage]);

  // Touch/swipe handling for mobile
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distanceX = touchStart.x - touchEnd.x;
    const distanceY = touchStart.y - touchEnd.y;
    const isLeftSwipe = distanceX > 50;
    const isRightSwipe = distanceX < -50;
    const isUpSwipe = distanceY > 50;
    const isDownSwipe = distanceY < -50;

    if (Math.abs(distanceX) > Math.abs(distanceY)) {
      // Horizontal swipe for navigation
      if (isLeftSwipe) {
        navigatePage('next');
      } else if (isRightSwipe) {
        navigatePage('prev');
      }
    } else if (fullscreen.zoom > 0.5) {
      // Vertical swipe for panning when zoomed
      if (isUpSwipe) {
        handlePan(0, -20);
      } else if (isDownSwipe) {
        handlePan(0, 20);
      }
    }
  };

  if (!pages.length) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Keine Seiten verfügbar
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Diese Foto-Anleitung enthält noch keine Seiten.
        </Typography>
      </Box>
    );
  }

  // Component for rendering individual images with authentication
  const AuthenticatedImage: React.FC<{
    page: ManualPage;
    thumbnail?: boolean;
    fullscreen?: boolean;
    onLoad?: () => void;
  }> = ({ page, thumbnail = false, fullscreen = false, onLoad }) => {
    const cacheKey = `${page.id}-${thumbnail}`;
    const imageUrl = loadedImageUrls.get(cacheKey);
    const isLoading = imageLoadingStates.has(cacheKey);
    const hasError = imageLoadErrors.has(cacheKey);

    // Start loading the image if not already loaded/loading
    useEffect(() => {
      loadImage(page.id, thumbnail);
    }, [page.id, thumbnail, loadImage]);

    if (hasError) {
      return (
        <Box
          sx={{
            width: '100%',
            height: thumbnail ? 150 : 300,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            border: '1px dashed',
            borderColor: 'grey.300',
            gap: 1,
          }}
        >
          <Typography variant="body2" color="error" textAlign="center">
            Bild konnte nicht geladen werden
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              // Clear error and retry loading
              setImageLoadErrors(prev => {
                const newSet = new Set(prev);
                newSet.delete(cacheKey);
                return newSet;
              });
              // Clear from cache to force reload
              imageUrlCache.delete(cacheKey);
              loadedImageUrls.delete(cacheKey);
              loadImage(page.id, thumbnail);
            }}
          >
            Wiederholen
          </Button>
        </Box>
      );
    }

    if (isLoading || !imageUrl) {
      return (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={thumbnail ? 150 : 300}
          animation="wave"
        />
      );
    }

    return (
      <img
        src={imageUrl}
        alt={`Seite ${page.page_number}`}
        style={{
          width: fullscreen ? '100%' : '100%',
          height: fullscreen ? '100%' : (thumbnail ? 150 : 300),
          objectFit: fullscreen ? 'contain' : 'cover',
          display: 'block',
        }}
        onLoad={onLoad}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {viewMode === 'gallery' ? (
        <ImageList 
          cols={isMobile ? 2 : 4} 
          gap={8}
          sx={{ margin: 0 }}
        >
          {pages.map((page, index) => (
            <ImageListItem 
              key={page.id}
              sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              onClick={() => openFullscreen(index)}
            >
              <AuthenticatedImage page={page} thumbnail={true} />
              <ImageListItemBar
                title={`Seite ${page.page_number}`}
                actionIcon={
                  <IconButton 
                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      openFullscreen(index);
                    }}
                  >
                    <FullscreenIcon />
                  </IconButton>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      ) : (
        <Grid container spacing={2}>
          {pages.map((page, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={page.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { 
                    transform: 'scale(1.02)',
                    boxShadow: 3,
                  }
                }}
                onClick={() => openFullscreen(index)}
              >
                <AuthenticatedImage page={page} thumbnail={true} />
                <CardContent>
                  <Typography variant="body2" fontWeight="medium">
                    Seite {page.page_number}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Fullscreen Modal */}
      <Dialog
        open={fullscreen.open}
        onClose={closeFullscreen}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'black',
            color: 'white',
          }
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100vh' }}>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            p: 2,
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 2,
          }}>
            <Typography variant="h6">
              {currentPage ? `Seite ${currentPage.page_number} von ${pages.length}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton color="inherit" onClick={() => handleZoom('out')} disabled={fullscreen.zoom <= 0.5}>
                <ZoomOutIcon />
              </IconButton>
              <IconButton color="inherit" onClick={() => handleZoom('fit')}>
                <FitScreenIcon />
              </IconButton>
              <IconButton color="inherit" onClick={() => handleZoom('in')} disabled={fullscreen.zoom >= 3}>
                <ZoomInIcon />
              </IconButton>
              <IconButton color="inherit" onClick={closeFullscreen}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Image Container */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {currentPage && (
              <Box
                sx={{
                  transform: `scale(${fullscreen.zoom}) translate(${fullscreen.pan.x}px, ${fullscreen.pan.y}px)`,
                  transition: 'transform 0.1s ease-out',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& img': {
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      width: 'auto',
                      height: 'auto',
                    }
                  }}
                >
                  <AuthenticatedImage page={currentPage} thumbnail={false} fullscreen={true} />
                </Box>
              </Box>
            )}
          </Box>

          {/* Navigation Controls */}
          {pages.length > 1 && (
            <>
              <Fab
                color="primary"
                size="medium"
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  opacity: fullscreen.currentPageIndex > 0 ? 1 : 0.3,
                }}
                onClick={() => navigatePage('prev')}
                disabled={fullscreen.currentPageIndex <= 0}
              >
                <PrevIcon />
              </Fab>
              
              <Fab
                color="primary"
                size="medium"
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  opacity: fullscreen.currentPageIndex < pages.length - 1 ? 1 : 0.3,
                }}
                onClick={() => navigatePage('next')}
                disabled={fullscreen.currentPageIndex >= pages.length - 1}
              >
                <NextIcon />
              </Fab>
            </>
          )}

          {/* Pan Controls (visible when zoomed) */}
          {fullscreen.zoom > 0.5 && (
            <Box sx={{
              position: 'absolute',
              bottom: 80,
              right: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}>
              <Fab size="small" onClick={() => handlePan(0, 20)} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <UpIcon />
              </Fab>
              <Fab size="small" onClick={() => handlePan(0, -20)} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <DownIcon />
              </Fab>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};